import React, { useEffect } from "react";
import { useAuth } from "../contexts/auth-context";
import { useNavigate } from "react-router-dom";
import LoadingScreen from "./LoadingScreen";
import { UserRole } from "../utils/role-permissions";

interface RoleGuardProps {
  children: React.ReactNode;
  allowedRoles: UserRole[];
  fallbackPath?: string;
}

/**
 * RoleGuard component that protects routes based on user roles.
 * Prevents unauthorized access even when users navigate directly via URL.
 */
const RoleGuard: React.FC<RoleGuardProps> = ({
  children,
  allowedRoles,
  fallbackPath = "/"
}) => {
  const { user, isLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Check if user role is allowed (must be done before early returns to avoid conditional hooks)
  const hasRequiredRole = user && allowedRoles.includes(user.role as UserRole);

  // Redirect to fallback path if user doesn't have required role
  useEffect(() => {
    if (isAuthenticated && user && !hasRequiredRole) {
      navigate(fallbackPath, { replace: true });
    }
  }, [isAuthenticated, user, hasRequiredRole, fallbackPath, navigate]);

  // Show loading screen while checking authentication
  if (isLoading) {
    return <LoadingScreen message="Vérification des permissions..." />;
  }

  // If not authenticated, this should be handled by the RefineAuthWrapper
  if (!isAuthenticated || !user) {
    return <LoadingScreen message="Redirection vers la page de connexion..." />;
  }

  // Show loading while redirecting unauthorized users
  if (!hasRequiredRole) {
    return <LoadingScreen message="Redirection en cours..." />;
  }

  // User has required role, render children
  return <>{children}</>;
};

export default RoleGuard;
